import {
  useContext,
  useState,
  useEffect,
} from 'react';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { TypographyProps } from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';

import {
  EDIT_PANEL_SCOPE,
  EDIT_PANEL_EVENT_TYPE,
} from '#common/constants/addons';
import {
  useGetCollectionsForUserMutation,
} from '#services/collection';
import {
  useBulkEditProductsMutation,
  useDeleteProductsMutation,
} from '#services/product';
import { productBulkEditValidations } from '#common/validation';
import useForm from '#utils/use-form';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import DropdownButton from './DropdownButton';
import {
  EDIT_PANEL_MAPPING,
} from './BulkEditComponents';
import type { EditPanelProps } from './BulkEditComponents';

interface CollectionOption {
  id?: number | string;
  name?: string;
  [key: string]: unknown;
}

interface ProductBulkEditBarProps {
  selected: Array<number | string>;
  misc: { pageSize?: number; [key: string]: unknown };
}

export default function ProductBulkEditBar(props: ProductBulkEditBarProps) {
  const {
    selected,

    misc,
  } = props;

  const {
    values,
    setValues,
    isValid,
    errors,
    errorMessages,
    handleChange,
    handleSubmit,
    addSubmitListener,
  } = useForm({
    initialState: {
      diff: {},
      availableCollections: [],
    },
    validations: productBulkEditValidations as Parameters<typeof useForm>[0]['validations'],
  });

  const [bulkEditProducts] = useBulkEditProductsMutation();
  const [deleteProducts] = useDeleteProductsMutation();
  const [selectWhole, setSelectWhole] = useState(false);
  const [selectedPanel, setSelectedPanel] = useState('');
  const [getCollectionsForUser] = useGetCollectionsForUserMutation();

  const allSelectedInPage = (misc.pageSize === selected.length);
  const selectedNothing = (selected.length === 0);

  const {
    showBackdrop,
    hideBackdrop,

    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,
  } = useContext(FeedbackUIContext)!;

  const handleUpdateProducts = (submittedValues: Record<string, unknown>, _isValid: boolean) => {
    setDialogData({
      title: 'Are you sure?',
      text: 'You\'re about to update the selected products. This update cannot be stopped once it has started.',
      disagree: 'Cancel',
      agree: 'Update Products',
      onDisagree: () => hideDialog(),
      onAgree: () => {
        hideDialog();
        const postData: Record<string, unknown> = {
          ...misc,

          selected,
          diffs: [submittedValues.diff],
          selectWhole,
        };

        if (selectWhole) {
          delete postData.selected;
        }

        bulkEditProducts(postData)
          .then((res) => {
            simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], 'Products were successfully updated.');
            setSelectedPanel('');
            handleChange(null, {
              name: 'diff',
              value: {},
            });
          }).catch((_e) => {
            //
          }).finally(() => {
            //
          });
      },
    });
    showDialog();
  };
  addSubmitListener(handleUpdateProducts);

  useEffect(() => {
    void getCollectionsForUser()
      .then((res) => {
        simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0]);
        setValues({
          availableCollections: ('data' in res ? res.data : undefined),
        });
      });
  }, []);

  useEffect(() => {
    if (!allSelectedInPage && selectWhole) {
      setSelectWhole(false);
    } else if (selectedNothing) {
      // clear selected panel on hidding the edit bar
      setSelectedPanel('');
    }
  }, [selected]);

  if (selectedNothing) {
    return null;
  }

  const handleWholeSelect = (_event?: unknown) => {
    setSelectWhole(true);
  };

  const toggleEditPanel = (_event: unknown, newValue: unknown) => {
    setSelectedPanel(newValue as string);
  };

  const handleDeleteProducts = (_event?: unknown, _data?: unknown) => {
    setDialogData({
      title: 'Delete forever',
      text: selectWhole
        ? 'All products in this search criteria will be deleted?'
        : `Delete ${selected.length} selected products?`,
      agree: 'Delete Forever',
      disagree: 'Cancel',
      onDisagree: () => hideDialog(),
      onAgree: () => {
        hideDialog();
        showBackdrop();

        void deleteProducts({
          ...misc,
          selected,
          selectWhole,
        }).then((res) => {
          simpleSnackbar(res as unknown as Parameters<typeof simpleSnackbar>[0], 'Products were deleted successfully.');
        }).finally(() => {
          hideBackdrop();
        });
      },
    });
    showDialog();
  };

  const renderAddToCollection = (options: { setOpen: (open: boolean) => void }) => {
    const {
      setOpen,
    } = options;

    const [selectedCollectionIds, setSelectedCollectionIds] = useState<Array<number | string>>([]);
    const availableCollections = values.availableCollections as CollectionOption[];
    return (
      <Paper sx={{ py: 1, px: 2 }}>
        <FormGroup>
          {availableCollections.map((ac) => {
            return (
              <FormControlLabel
                key={ac.id}
                control={(
                  <Checkbox
                    checked={selectedCollectionIds.includes(ac.id as number | string)}
                    onChange={(e, checked) => {
                      const newValue = selectedCollectionIds.filter((cid) => cid !== ac.id);
                      if (checked) {
                        newValue.push(ac.id as number | string);
                      }

                      setSelectedCollectionIds(newValue);
                      handleChange(e, {
                        name: 'diff',
                        value: {
                          data: newValue,
                          event: 'set',
                          eventType: EDIT_PANEL_EVENT_TYPE.ADD,
                          scope: EDIT_PANEL_SCOPE.COLLECTION,
                        },
                      });
                    }}
                  />
                )}
                label={ac.name}
              />
            );
          })}
        </FormGroup>
        <Divider />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            bgcolor: 'background.paper',
            borderRadius: 1,
            pt: 1,
          }}
        >
          <div />
          <Box>
            <Button
              component="label"
              disabled={!isValid}
              onClick={(e) => {
                setOpen(false);
                handleSubmit(e);
              }}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Paper>
    );
  };

  // render panels
  const EditPanel = EDIT_PANEL_MAPPING[selectedPanel] || (() => null);

  const renderToolbarActions = () => {
    if (selectedPanel) {
      return (
        <Box sx={{ marginLeft: 'auto' }}>
          <Button
            variant="text"
            onClick={(e) => toggleEditPanel(e, null)}
          >
            Cancel
          </Button>

          <Button
            sx={{
              '&.Mui-disabled': {
                color: 'grey',
                background: 'lightgrey',
              },
            }}
            variant="contained"
            disabled={!isValid}
            onClick={handleSubmit}
          >
            Update Products
          </Button>
        </Box>
      );
    }

    return (
      <Stack
        direction="row"
      >
        <DropdownButton
          sx={{ color: '#fff' }}
          text="Edit"
          options={[{
            value: 'title',
            text: 'Title',
          }, {
            value: 'description',
            text: 'Description',
          }, {
            value: 'shippingWindow',
            text: 'Shipping Window',
          }, {
            value: 'customization',
            text: 'Customization',
          }]}
          onChange={toggleEditPanel}
        >
          Edit
        </DropdownButton>

        <DropdownButton
          sx={{ color: '#fff' }}
          text="Add to collection"
          renderer={renderAddToCollection}
        >
          Add to collection
        </DropdownButton>

        <DropdownButton
          sx={{ color: '#fff' }}
          text="More actions"
          options={[{
            value: 'delete',
            text: 'Delete forever',
          }]}
          onChange={handleDeleteProducts}
        >
          More actions
        </DropdownButton>
      </Stack>
    );
  };

  return (
    <>
      <AppBar
        position="static"
        sx={{
          mb: 2,
          bgcolor: 'black',
        }}
      >
        <Toolbar>
          <Typography variant={'h7' as TypographyProps['variant']} component="div">
            {
              selectWhole
                ? 'All items selected'
                : `${selected.length} selected`
            }
          </Typography>

          <Button
            variant="text"
            onClick={handleWholeSelect}
            sx={{
              display: (allSelectedInPage && !selectWhole)
                ? 'block'
                : 'none',
            }}
          >
            select all matching
          </Button>

          <Divider
            sx={{ m: 2, bgcolor: 'grey' }}
            orientation="vertical"
            flexItem
          />

          {
            renderToolbarActions()
          }
        </Toolbar>
      </AppBar>

      <Box sx={{ mx: 1 }}>
        <EditPanel
          onChange={handleChange as unknown as EditPanelProps['onChange']}
          errors={errors}
          errorMessages={errorMessages}
        />
      </Box>
    </>
  );
}
